import React, {useEffect, useState} from 'react';
import {AnimatePresence, motion} from "framer-motion";
import {useTranslation} from "react-i18next";

// Modale d'accueil pour un visiteur arrivé via un lien de parrainage (nouveau joueur uniquement).
// Affichée au-dessus du StepWelcome (et du welcome pénos). Le filleul ne connaît PAS GOJO : la modale
// raconte le double cadeau — la récompense de bienvenue (rewardTitle) débloquée à l'inscription, puis
// le lot du gagnant à décrocher en jouant le prono (gameRewardLabel). Le pseudo du parrain n'est plus mis
// en avant. Non bloquante : le CTA la ferme et laisse le welcome normal dessous.
//
// Le reveal de la récompense est joué comme un DROP de jeu vidéo (loot box) : anticipation (coffre qui
// pulse) → burst (flash + rayons + particules + shake) → la récompense claque à l'écran. Piloté par un
// simple flag `revealed` armé ~850 ms après l'ouverture.

// Rayons de lumière « légendaire » derrière la récompense (repeating-conic masqué en disque qui fond).
const RAYS = 'repeating-conic-gradient(from 0deg, rgba(253,224,71,0.22) 0deg 9deg, transparent 9deg 22deg)';
const RAY_MASK = 'radial-gradient(circle, #000 25%, transparent 72%)';
// Angles des particules du burst (giclée radiale).
const SPARKS = Array.from({length: 16}, (_, i) => (i / 16) * Math.PI * 2);

export const ReferralWelcomeModal = ({isOpen, rewardTitle, gameRewardLabel, onClose}) => {
    const {t} = useTranslation();
    // Anticipation → reveal. Ré-armé à chaque ouverture (et remis à zéro à la fermeture).
    const [revealed, setRevealed] = useState(false);

    useEffect(() => {
        if (!isOpen) {
            setRevealed(false);
            return;
        }
        const id = setTimeout(() => setRevealed(true), 850);
        return () => clearTimeout(id);
    }, [isOpen]);

    return (
        <AnimatePresence>
            {isOpen && (
                <motion.div
                    className="fixed inset-0 z-[90] flex items-end sm:items-center justify-center p-4"
                    initial={{opacity: 0}} animate={{opacity: 1}} exit={{opacity: 0}}
                >
                    <div className="absolute inset-0 bg-slate-900/80 backdrop-blur-sm" onClick={onClose}/>

                    <motion.div
                        initial={{y: 60, opacity: 0, scale: 0.96}}
                        animate={{y: 0, opacity: 1, scale: 1}}
                        exit={{y: 40, opacity: 0}}
                        transition={{type: 'spring', damping: 22, stiffness: 260}}
                        className="relative z-10 w-full max-w-sm bg-brand-dark rounded-3xl p-7 overflow-hidden shadow-2xl text-center"
                    >
                        <div className="absolute -top-16 left-1/2 -translate-x-1/2 w-64 h-64 rounded-full bg-action-green/25 blur-[70px] pointer-events-none"/>

                        {/* Shake d'impact : tout le contenu tremble un coup au moment du burst. */}
                        <motion.div
                            className="relative z-10"
                            animate={revealed ? {x: [0, -5, 5, -4, 4, -2, 2, 0], y: [0, 3, -3, 2, -2, 0]} : {x: 0, y: 0}}
                            transition={{duration: 0.45, ease: 'easeOut'}}
                        >
                            <motion.div
                                className="inline-flex items-center gap-1.5 px-3 py-1 mb-4 rounded-full bg-action-green/15 border border-action-green/30"
                                initial={{opacity: 0, y: 10}} animate={{opacity: 1, y: 0}} transition={{delay: 0.15}}
                            >
                                <span className="text-sm leading-none">✨</span>
                                <span className="text-[10px] font-black text-action-green uppercase tracking-widest">
                                    {t('referral.modal_badge', 'Offre exclusive')}
                                </span>
                            </motion.div>

                            <motion.h3
                                className="text-2xl font-black text-white uppercase tracking-tight leading-tight mb-5"
                                initial={{opacity: 0, y: 12}} animate={{opacity: 1, y: 0}} transition={{delay: 0.25}}
                            >
                                {t('referral.welcome_banner_generic', 'Un ami t\'invite')}
                                {' '}🎉
                            </motion.h3>

                            {/* SCÈNE DU DROP — hauteur fixe pour que le passage coffre → récompense ne fasse pas sauter le layout. */}
                            <div className="relative h-40 mb-3 flex items-center justify-center">
                                {/* Rayons rotatifs (apparaissent au burst, tournent en boucle derrière la récompense). */}
                                <AnimatePresence>
                                    {revealed && (
                                        <motion.div
                                            key="rays" aria-hidden
                                            className="absolute w-64 h-64 pointer-events-none"
                                            style={{background: RAYS, WebkitMaskImage: RAY_MASK, maskImage: RAY_MASK}}
                                            initial={{opacity: 0, scale: 0.2, rotate: 0}}
                                            animate={{opacity: 0.9, scale: 1, rotate: 360}}
                                            exit={{opacity: 0}}
                                            transition={{
                                                opacity: {duration: 0.4},
                                                scale: {type: 'spring', damping: 12, stiffness: 120},
                                                rotate: {duration: 16, ease: 'linear', repeat: Infinity},
                                            }}
                                        />
                                    )}
                                </AnimatePresence>

                                {/* Halo qui pulse au moment du reveal. */}
                                {revealed && (
                                    <motion.div
                                        aria-hidden
                                        className="absolute w-40 h-40 rounded-full bg-yellow-400/30 blur-2xl pointer-events-none"
                                        initial={{opacity: 0, scale: 0.6}}
                                        animate={{opacity: [0, 0.85, 0.45], scale: [0.6, 1.15, 1]}}
                                        transition={{duration: 0.9, ease: 'easeOut'}}
                                    />
                                )}

                                {/* Flash d'impact. */}
                                <AnimatePresence>
                                    {revealed && (
                                        <motion.div
                                            key="flash" aria-hidden
                                            className="absolute w-48 h-48 rounded-full bg-white pointer-events-none"
                                            style={{filter: 'blur(10px)'}}
                                            initial={{opacity: 0, scale: 0.3}}
                                            animate={{opacity: [0, 0.9, 0], scale: [0.3, 1.5, 1.7]}}
                                            transition={{duration: 0.5, ease: 'easeOut'}}
                                        />
                                    )}
                                </AnimatePresence>

                                {/* Particules qui giclent radialement au burst. */}
                                {revealed && SPARKS.map((a, i) => (
                                    <motion.span
                                        key={`spark-${i}`} aria-hidden
                                        className={`absolute rounded-full ${i % 2 ? 'w-1.5 h-1.5 bg-yellow-300' : 'w-1 h-1 bg-action-green'}`}
                                        initial={{opacity: 1, scale: 1, x: 0, y: 0}}
                                        animate={{
                                            opacity: [1, 1, 0],
                                            scale: [0.8, 1.2, 0.2],
                                            x: Math.cos(a) * (95 + (i % 3) * 15),
                                            y: Math.sin(a) * (95 + (i % 3) * 15),
                                        }}
                                        transition={{duration: 0.75, ease: 'easeOut'}}
                                    />
                                ))}

                                {/* Coffre d'anticipation : pulse + wiggle, puis explose (scale up + fade) au burst. */}
                                <AnimatePresence>
                                    {!revealed && (
                                        <motion.div
                                            key="orb" aria-hidden
                                            className="absolute flex items-center justify-center"
                                            initial={{opacity: 0, scale: 0.6}}
                                            animate={{opacity: 1, scale: 1, rotate: [-7, 7, -7]}}
                                            exit={{opacity: 0, scale: 1.8}}
                                            transition={{
                                                opacity: {duration: 0.3},
                                                scale: {type: 'spring', damping: 14},
                                                rotate: {duration: 0.5, repeat: Infinity, ease: 'easeInOut'},
                                            }}
                                        >
                                            <div className="absolute w-24 h-24 rounded-full bg-action-green/30 blur-xl"/>
                                            <motion.span
                                                className="relative text-6xl"
                                                animate={{scale: [1, 1.14, 1]}}
                                                transition={{duration: 0.55, repeat: Infinity, ease: 'easeInOut'}}
                                            >
                                                🎁
                                            </motion.span>
                                        </motion.div>
                                    )}
                                </AnimatePresence>

                                {/* La récompense — claque à l'écran (gros overshoot) une fois le coffre ouvert. */}
                                <AnimatePresence>
                                    {revealed && (
                                        <motion.div
                                            key="reward"
                                            className="relative z-10 w-full"
                                            initial={{opacity: 0, scale: 0.3, y: 8}}
                                            animate={{opacity: 1, scale: 1, y: 0}}
                                            transition={{type: 'spring', damping: 11, stiffness: 210, delay: 0.12}}
                                        >
                                            {/* Récompense de bienvenue = l'offre configurée par l'établissement, jamais un texte en dur. */}
                                            <div className="rounded-2xl bg-white/10 border border-yellow-400/40 px-4 py-4 backdrop-blur-sm shadow-[0_0_30px_rgba(250,204,21,0.25)]">
                                                <p className="text-[10px] font-black text-yellow-400 uppercase tracking-widest mb-1.5">
                                                    {t('referral.modal_reward_intro', 'Ta récompense de bienvenue')}
                                                </p>
                                                <motion.p
                                                    className="text-xl font-black text-white leading-tight"
                                                    initial={{scale: 0.6}} animate={{scale: 1}}
                                                    transition={{type: 'spring', damping: 8, stiffness: 240, delay: 0.24}}
                                                >
                                                    {rewardTitle || t('referral.welcome_reward', 'Une récompense offerte')}
                                                </motion.p>
                                            </div>
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </div>

                            {/* Mécanique + fine print + CTA : n'apparaissent qu'APRÈS le reveal, pour ne pas voler la vedette au drop. */}
                            <motion.div
                                initial={{opacity: 0, y: 12}}
                                animate={revealed ? {opacity: 1, y: 0} : {opacity: 0, y: 12}}
                                transition={{delay: 0.2, duration: 0.35}}
                            >
                                {/* Le filleul ne connaît pas GOJO : on explicite le mécanisme (inscription → cadeau de bienvenue,
                                    puis jeu → lot du gagnant nommé). Fallback générique si le lot n'est pas configuré. */}
                                <p className="text-[13px] text-white/70 font-semibold leading-snug mb-3">
                                    {gameRewardLabel
                                        ? t('referral.modal_reward_howto', {prize: gameRewardLabel, defaultValue: 'Inscris-toi pour la recevoir par mail — puis pronostique le match et tente de gagner {{prize}} 🏆'})
                                        : t('referral.modal_reward_howto_generic', 'Inscris-toi pour la recevoir par mail — puis pronostique le match et tente de décrocher le lot du gagnant 🏆')}
                                </p>

                                <p className="text-[11px] text-white/40 font-semibold uppercase tracking-wide mb-6">
                                    {t('referral.new_players_only', 'Offre réservée aux nouveaux joueurs')}
                                </p>

                                <button
                                    onClick={onClose}
                                    className="w-full py-4 bg-action-green text-white font-black rounded-xl text-base uppercase tracking-wide shadow-lg hover:brightness-110 active:scale-98 transition-transform"
                                >
                                    {t('referral.modal_cta', 'C\'est parti !')}
                                </button>
                            </motion.div>
                        </motion.div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

export default ReferralWelcomeModal;
